import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import Footer from "./Footer";
import Sidebar from "./sidebar/Sidebar";
import Header from "./header/Header";
import TermsAcceptanceModal from "../../components/TermsAcceptanceModal";
import OnboardingPage from "../../components/OnboardingPage";
import Spinner from "../shared-component/Spinner";
import useProjectStore from "../../store/useProjectStore";

export default function Layout({
    auth,
    isAdminOrSubadmin,
    onOpenFeedback,
    agents,
    projects,
    quota,
    onProjectsChanged,
    projectsError, //true when the projects API failed
    onRetryProjects, //function to call the API again
}) {
    const [termsLoading, setTermsLoading] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    // NEW: user has zero projects -> forget any old saved project ID Only runs when the API succeeded and returned zero projects
    useEffect(() => {
        if (projects !== null && projects.length === 0) {
            useProjectStore.getState().clearProjects();
        }
    }, [projects]);

    // The header's project dropdown and every section that reads a project
    // now take it from the store, so the list App.js fetched has to land
    // there. One fetch, one copy — the sections no longer each ask for it.
    useEffect(() => {
        if (projects === null) return;
        if (projects.length === 0) return; // handled by clearProjects above
        useProjectStore.getState().setProjects(projects);
    }, [projects]);

    useEffect(() => {
        useProjectStore.getState().setQuota(quota);
    }, [quota]);

    const handleAcceptTerms = async () => {
        try {
            setTermsLoading(true);
            await auth.acceptTerms();
        } catch (err) {
            console.error("Failed to accept terms:", err);
        } finally {
            setTermsLoading(false);
        }
    };

    // Gate 1: terms must be accepted first — takes priority over everything.
    if (!auth.termsAccepted) {
        return (
            <TermsAcceptanceModal
                onAccept={handleAcceptTerms}
                onDecline={auth.logout}
                loading={termsLoading}
            />
        );
    }

    // NEW: API failed (offline, server down) — show a message, don't guess.
    // Must stay ABOVE the spinner check, because projects stays null after a failure.
    if (projectsError) {
        return (
            <div className="error-page text__center mw__672" role="alert">
                <h1 className="h2 fc__0E1218 mb__16">Couldn't load your workspace</h1>
                <p className="fb__16 fc__5F636A mb__10">Please check your internet connection and try again.</p>
                <button className="common__btn common__transp__btn" onClick={onRetryProjects}>Retry</button>
            </div>
        );
    }

    // ADD THIS — projects haven't loaded yet, don't decide anything
    if (projects === null) {
        return <Spinner />; // or a spinner component if you have one
    }

    // Gate 2: user needs at least one complete project before using the app.
    //
    // Guests are exempt: they have no projects and the project API is closed to
    // them, so an empty list is their normal state, not an unfinished setup.
    // Without this they land on the onboarding screen and are asked to create
    // something the server will refuse — a dead end on first sign-in.
    const isGuest = auth.user?.role === "guest";
    const incompleteProject = projects?.find((p) => !p.isComplete) || null;
    const needsOnboarding =
        !isGuest && (projects?.length === 0 || !!incompleteProject);

    if (needsOnboarding) {
        return (
            <OnboardingPage
                incompleteProject={incompleteProject}
                userName={auth.user?.fullName || auth.user?.username || ""}
                showAdminLink={isAdminOrSubadmin}
                onDone={onProjectsChanged}
                onLogout={auth.logout}
            />
        );
    }

    // Gates passed — render the real app shell.
    return (
        <div className="app__shell">
            <div className="wrapper__app__main">
                <Sidebar
                    auth={auth}
                    isAdminOrSubadmin={isAdminOrSubadmin}
                    onOpenFeedback={onOpenFeedback}
                    agents={agents}
                    projects={projects}
                    open={menuOpen}
                    onClose={() => setMenuOpen(false)}
                    collapsed={collapsed} onToggleCollapse={() => setCollapsed(c => !c)}
                />
                
                <main className={`app-main ${collapsed ? "app-main--collapsed" : ""}`}>
                    {menuOpen && (
                        <div className="sidebar__overlay" onClick={() => setMenuOpen(false)} />
                    )}
                    <Header onNewRun={() => { }} onMenuClick={()=>setMenuOpen((o) => !o)} />
                    <div className="wrapper__app__main__content">
                        <Outlet />
                    </div>
                </main>
            </div>
            <Footer />
        </div>
    );
}